// Stock and sales read API contracts (`GET /stock/*`, `GET /sales/*`, plan §5.3–§5.4) and
// the ledger projection repair (`POST /stock/projection/repair`). Wire format: snake_case
// fields, quantities as decimal strings, money as integer cents, instants as ISO-8601 UTC
// strings, trading days and best-before dates as `YYYY-MM-DD`.
//
// A product's states are **attributes of its stock**, never separate products: levels are
// grouped by product, with the per-state split inside (a product sold under four state
// SKUs is one row here).
import { z } from 'zod';
import { stateKeySchema } from '../catalogue/index.js';
import { LOCATION_TYPES } from '../fefo/fefo.js';
import { ACTOR_KINDS, PROVENANCES, REASON_CODES } from '../ledger/index.js';
import { qtyString } from '../quantity/index.js';
import { isPlainDate } from '../time/index.js';
import { uomCode } from '../uom/index.js';
import type { OrderType, VatLineKind, VatRateCode } from '../vat/vat.js';

// The VAT module re-exports `VAT_MODES` from this API module, so importing its values here
// would be a cycle. These lists are its `ORDER_TYPES`, `VAT_LINE_KINDS` and
// `VAT_RATE_CODES` (stock.test.ts proves they stay equal).
/** Same values as `ORDER_TYPES` in `@enp/domain/vat`. */
export const TICKET_ORDER_TYPES = [
  'takeaway',
  'eat_in',
  'delivery',
] as const satisfies readonly OrderType[];
/** Same values as `VAT_LINE_KINDS` in `@enp/domain/vat`. */
export const TICKET_LINE_KINDS = ['goods', 'delivery'] as const satisfies readonly VatLineKind[];
/** Same values as `VAT_RATE_CODES` in `@enp/domain/vat`. */
export const TICKET_VAT_CODES = [
  'standard',
  'zero_rated',
] as const satisfies readonly VatRateCode[];

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const cents = z.int();
const count = z.int().min(0);

/** Largest page the stock and sales lists return. */
export const STOCK_PAGE_MAX = 200;
/** Page size when `limit` is not given. */
export const STOCK_PAGE_DEFAULT = 50;
const limit = z.coerce.number().int().min(1).max(STOCK_PAGE_MAX).default(STOCK_PAGE_DEFAULT);
const cursor = z.string().min(1).max(512).optional();

/** `:id` path parameter of stock and sales routes. */
export const stockIdParamsSchema = z.object({ id: z.uuid() });

/** A product reference in stock responses. */
export const stockProductRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  base_uom: uomCode,
});

/** A location reference in stock responses. */
export const stockLocationRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  type: z.enum(LOCATION_TYPES),
});

// ---------------------------------------------------------------------------------------
// Levels

/**
 * `GET /stock/levels` query. `product`, `location`, `state` and `best_before_by` narrow
 * which balance rows count; products with nothing on hand (every row zero) are left out.
 * `q` matches the product name (case-insensitive, anywhere in it). Ordered by product
 * name, then id; `cursor` is the previous page's `next_cursor` (same filters only).
 */
export const stockLevelsQuerySchema = z.object({
  product: z.uuid().optional(),
  q: z.string().trim().min(1).max(100).optional(),
  location: z.uuid().optional(),
  state: stateKeySchema.optional(),
  /**
   * Only lots whose best-before is on or before this day ("near expiry": today + a few
   * days). Lot-less stock has no best-before, so it never counts under this filter.
   */
  best_before_by: plainDate.optional(),
  limit,
  cursor,
});
/** Parsed `GET /stock/levels` query. */
export type StockLevelsQuery = z.infer<typeof stockLevelsQuerySchema>;

/** One state's level within a product. */
export const stockStateLevelSchema = z.object({ state: stateKeySchema, qty: qtyString });

/** One location's level within a product (across its states and lots). */
export const stockLocationLevelSchema = z.object({
  location: stockLocationRefSchema,
  qty: qtyString,
});

/**
 * One product's stock. `total` is Σ over its states (one product, never one per state);
 * `states` are in the order of the product's state rule (unknown states last, A–Z);
 * zero states and locations are left out. Levels may be negative (sold beyond stock).
 */
export const stockProductLevelSchema = z.object({
  product: stockProductRefSchema,
  total: qtyString,
  states: z.array(stockStateLevelSchema),
  locations: z.array(stockLocationLevelSchema),
  /** Lots with a non-zero level (lot-less stock is not counted). */
  lot_count: count,
  /**
   * The soonest best-before among the lots counted (a level above zero), or `null` when no
   * counted lot has one ("what expires first?").
   */
  soonest_best_before: plainDate.nullable(),
});
/** One product's stock. */
export type StockProductLevel = z.infer<typeof stockProductLevelSchema>;

/** `GET /stock/levels` response. */
export const stockLevelsResponseSchema = z.object({
  items: z.array(stockProductLevelSchema),
  /** Pass as `cursor` for the next page; `null` on the last page. */
  next_cursor: z.string().nullable(),
});
/** Response of `GET /stock/levels`. */
export type StockLevelsResponse = z.infer<typeof stockLevelsResponseSchema>;

// ---------------------------------------------------------------------------------------
// Locations (for filters)

/** A stock location with its branch. */
export const stockLocationSchema = stockLocationRefSchema.extend({
  branch: z.object({ id: z.uuid(), name: z.string() }),
});
/** A stock location. */
export type StockLocation = z.infer<typeof stockLocationSchema>;

/**
 * `GET /stock/locations` response: the live locations of the account's normal branches
 * (a sales replay's hidden branch is left out, D-036): the default branch first, then by
 * branch name, then location name.
 */
export const stockLocationsResponseSchema = z.object({ items: z.array(stockLocationSchema) });
/** Response of `GET /stock/locations`. */
export type StockLocationsResponse = z.infer<typeof stockLocationsResponseSchema>;

// ---------------------------------------------------------------------------------------
// Lots of one product

/** Where a lot came from (`lot.source`). */
export const LOT_SOURCES = [
  'production',
  'receipt',
  'opening_count',
  'opening_unverified',
  'migration',
  'synthetic',
] as const;
/** Lot lifecycle (`lot.status`); the expiry state is per balance row, not per lot. */
export const LOT_STATUSES = ['active', 'on_hold', 'closed'] as const;

/** A lot in stock responses. */
export const stockLotSchema = z.object({
  id: z.uuid(),
  lot_code: z.string(),
  source: z.enum(LOT_SOURCES),
  status: z.enum(LOT_STATUSES),
  /** Invented by the sales replay; not real stock. */
  synthetic: z.boolean(),
  best_before: plainDate.nullable(),
  produced_at: instant.nullable(),
  supplier_lot_ref: z.string().nullable(),
});
/** A lot. */
export type StockLot = z.infer<typeof stockLotSchema>;

/** Why the level of a row is what it is: the latest movement that touched it. */
export const stockLastMovementSchema = z.object({
  id: z.uuid(),
  reason: z.enum(REASON_CODES),
  provenance: z.enum(PROVENANCES),
  occurred_at: instant,
  actor_type: z.enum(ACTOR_KINDS),
});

/** One non-zero balance row of a product: a lot (or lot-less stock) at a location, in a state. */
export const stockLotRowSchema = z.object({
  /** `null` for stock that is not tracked by lot. */
  lot: stockLotSchema.nullable(),
  location: stockLocationRefSchema,
  state: stateKeySchema,
  qty: qtyString,
  last_movement: stockLastMovementSchema.nullable(),
});
/** One balance row of a product. */
export type StockLotRow = z.infer<typeof stockLotRowSchema>;

/**
 * `GET /stock/products/:id/lots` response: every non-zero row, soonest best-before first
 * (lot-less last), then lot code, location name and state.
 */
export const stockProductLotsResponseSchema = z.object({
  product: stockProductRefSchema,
  rows: z.array(stockLotRowSchema),
});
/** Response of `GET /stock/products/:id/lots`. */
export type StockProductLotsResponse = z.infer<typeof stockProductLotsResponseSchema>;

// ---------------------------------------------------------------------------------------
// Movements (the ledger)

/**
 * `GET /stock/movements` query: `from`/`to` are trading days (inclusive), `reason` one
 * reason code, `location` one location, `q` part of the product name (case-insensitive).
 * Newest first (occurred_at, then id, descending).
 */
export const stockMovementsQuerySchema = z
  .object({
    product: z.uuid().optional(),
    lot: z.uuid().optional(),
    reason: z.enum(REASON_CODES).optional(),
    location: z.uuid().optional(),
    q: z.string().trim().min(1).max(100).optional(),
    from: plainDate.optional(),
    to: plainDate.optional(),
    limit,
    cursor,
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: '`from` must not be after `to`',
    path: ['from'],
  });
/** Parsed `GET /stock/movements` query. */
export type StockMovementsQuery = z.infer<typeof stockMovementsQuerySchema>;

/** One ledger movement with its source document and actor. */
export const stockMovementSchema = z.object({
  id: z.uuid(),
  idempotency_key: z.string(),
  product: stockProductRefSchema,
  lot: z.object({ id: z.uuid(), lot_code: z.string() }).nullable(),
  location: stockLocationRefSchema,
  state: stateKeySchema,
  qty_delta: qtyString,
  reason: z.enum(REASON_CODES),
  provenance: z.enum(PROVENANCES),
  source_doc: z.object({
    type: z.string(),
    id: z.string(),
    /**
     * The document's own number, when it has one people read: a receipt's printed number,
     * else the channel's id for it (review UI2A-215). `null` for other documents.
     */
    number: z.string().nullable().default(null),
  }),
  actor: z.object({
    type: z.enum(ACTOR_KINDS),
    id: z.string(),
    device_id: z.string().nullable(),
    /** The staff member's or admin's name, when the actor is one we know. */
    name: z.string().nullable(),
    /** The station the device belongs to, when recorded on one. */
    station: z.string().nullable(),
  }),
  occurred_at: instant,
  recorded_at: instant,
  trading_day: plainDate,
  /** Key of the movement this one reverses. */
  compensates_key: z.string().nullable(),
  /** The movement this one reverses, when it is in the ledger (review UI2A-215). */
  compensates: z
    .object({
      id: z.uuid(),
      reason: z.enum(REASON_CODES),
      occurred_at: instant,
      qty_delta: qtyString,
    })
    .nullable()
    .default(null),
});
/** One ledger movement. */
export type StockMovement = z.infer<typeof stockMovementSchema>;

/** `GET /stock/movements` response. */
export const stockMovementsResponseSchema = z.object({
  items: z.array(stockMovementSchema),
  next_cursor: z.string().nullable(),
});
/** Response of `GET /stock/movements`. */
export type StockMovementsResponse = z.infer<typeof stockMovementsResponseSchema>;

// ---------------------------------------------------------------------------------------
// Projection repair (admin, audited)

/** A balance row whose projection disagrees with the ledger. */
export const ledgerDifferenceSchema = z.object({
  product_id: z.uuid(),
  lot_id: z.uuid().nullable(),
  location_id: z.uuid(),
  state: stateKeySchema,
  /** Σ qty_delta of the movements (the truth). */
  ledger_qty: qtyString,
  /** What `stock_balance` held. */
  projection_qty: qtyString,
});
/** A projection difference. */
export type LedgerDifference = z.infer<typeof ledgerDifferenceSchema>;

/** `POST /stock/projection/repair` response: the rows rewritten from the ledger. */
export const stockProjectionRepairResponseSchema = z.object({
  /** Balance rows compared. */
  checked: count,
  repaired: count,
  differences: z.array(ledgerDifferenceSchema),
});
/** Response of `POST /stock/projection/repair`. */
export type StockProjectionRepairResponse = z.infer<typeof stockProjectionRepairResponseSchema>;

// ---------------------------------------------------------------------------------------
// Sales tickets

/** `sale_ticket.ticket_type`. */
export const SALE_TICKET_TYPES = ['sale', 'refund'] as const;
/** `sale_ticket.status`. */
export const SALE_TICKET_STATUSES = ['open', 'completed', 'cancelled'] as const;

const salesFilters = {
  from: plainDate.optional(),
  to: plainDate.optional(),
  channel: z.uuid().optional(),
  /** Part of the receipt number or the channel's own id (case-insensitive). */
  q: z.string().trim().min(1).max(100).optional(),
  status: z.enum(SALE_TICKET_STATUSES).optional(),
  type: z.enum(SALE_TICKET_TYPES).optional(),
};
const orderedDays = <T extends { from?: string; to?: string }>(q: T) =>
  !q.from || !q.to || q.from <= q.to;

/**
 * `GET /sales/tickets` query: `from`/`to` are trading days (inclusive), `channel` a channel
 * id, `q` part of the receipt number, `status` and `type` one each. Newest first
 * (occurred_at, then id, descending).
 */
export const salesTicketsQuerySchema = z
  .object({ ...salesFilters, limit, cursor })
  .refine(orderedDays, { message: '`from` must not be after `to`', path: ['from'] });
/** Parsed `GET /sales/tickets` query. */
export type SalesTicketsQuery = z.infer<typeof salesTicketsQuerySchema>;

/** `GET /sales/summary` query: the same filters as the ticket list. */
export const salesSummaryQuerySchema = z
  .object(salesFilters)
  .refine(orderedDays, { message: '`from` must not be after `to`', path: ['from'] });
/** Parsed `GET /sales/summary` query. */
export type SalesSummaryQuery = z.infer<typeof salesSummaryQuerySchema>;

/**
 * `GET /sales/summary`: totals of every ticket the filters match (not just a page).
 * Money in cents, VAT-inclusive; `total_cents` and `vat_cents` leave cancelled tickets out
 * (they don't count towards sales) and net refunds off (a refund's total is negative).
 */
export const salesSummarySchema = z.object({
  tickets: count,
  sales: count,
  refunds: count,
  cancelled: count,
  open: count,
  total_cents: cents,
  vat_cents: cents,
  /** Lines of the counted tickets' current revisions that map to no product. */
  unmapped_lines: count,
});
/** Response of `GET /sales/summary`. */
export type SalesSummary = z.infer<typeof salesSummarySchema>;

/** `GET /sales/channels`: the real channels the caller can see tickets of, by name. */
export const salesChannelsResponseSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string(), kind: z.string() })),
});
/** Response of `GET /sales/channels`. */
export type SalesChannelsResponse = z.infer<typeof salesChannelsResponseSchema>;

/** A channel reference; show `kind` by the connector registry's display name. */
export const saleTicketChannelSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.string(),
});

/** A ticket in a list. Money in cents, VAT-inclusive. */
export const saleTicketSummarySchema = z.object({
  id: z.uuid(),
  channel: saleTicketChannelSchema,
  external_id: z.string(),
  number: z.string().nullable(),
  ticket_type: z.enum(SALE_TICKET_TYPES),
  status: z.enum(SALE_TICKET_STATUSES),
  order_type: z.enum(TICKET_ORDER_TYPES),
  occurred_at: instant,
  trading_day: plainDate,
  /** The current revision; its lines are the ticket's lines. */
  revision: count,
  gross_cents: cents,
  discount_cents: cents,
  total_cents: cents,
  vat_cents: cents,
  /** Lines of the current revision. */
  line_count: count,
  /** Lines of the current revision that map to no product (reported, never guessed). */
  unmapped_line_count: count,
});
/** A ticket in a list. */
export type SaleTicketSummary = z.infer<typeof saleTicketSummarySchema>;

/** `GET /sales/tickets` response. */
export const salesTicketsResponseSchema = z.object({
  items: z.array(saleTicketSummarySchema),
  next_cursor: z.string().nullable(),
});
/** Response of `GET /sales/tickets`. */
export type SalesTicketsResponse = z.infer<typeof salesTicketsResponseSchema>;

/** One line of a ticket's current revision. */
export const saleTicketLineSchema = z.object({
  id: z.uuid(),
  line_ref: z.string(),
  revision: count,
  line_kind: z.enum(TICKET_LINE_KINDS),
  /** `null` when the line maps to no product. */
  product: stockProductRefSchema.nullable(),
  /** The state the channel item sold (a state SKU), when it names one. */
  state: stateKeySchema.nullable(),
  channel_mapping_id: z.uuid().nullable(),
  external_item_id: z.string().nullable(),
  external_variant_id: z.string().nullable(),
  description: z.string().nullable(),
  qty: qtyString,
  unit_price_cents: cents,
  gross_cents: cents,
  discount_cents: cents,
  total_cents: cents,
  vat_code: z.enum(TICKET_VAT_CODES),
  vat_cents: cents,
});
/** A ticket line. */
export type SaleTicketLine = z.infer<typeof saleTicketLineSchema>;

/** A payment on a ticket (a ticket can have several). */
export const saleTicketPaymentSchema = z.object({
  id: z.uuid(),
  method: z.string(),
  amount_cents: cents,
  external_ref: z.string().nullable(),
  paid_at: instant,
});

/**
 * One revision of a ticket: the source edited the ticket, and every line was written again
 * under the new number (the earlier lines are kept, never overwritten).
 */
export const saleTicketRevisionSchema = z.object({
  revision: count,
  line_count: count,
  total_cents: cents,
  vat_cents: cents,
});
/** A ticket revision. */
export type SaleTicketRevision = z.infer<typeof saleTicketRevisionSchema>;

/** `GET /sales/tickets/:id` response. */
export const saleTicketDetailSchema = saleTicketSummarySchema.extend({
  refund_of_ticket_id: z.uuid().nullable(),
  /** The sale a refund refunds (its receipt number, for "Refunds receipt 1-2003"). */
  refund_of: z
    .object({ id: z.uuid(), number: z.string().nullable(), external_id: z.string() })
    .nullable(),
  lines: z.array(saleTicketLineSchema),
  payments: z.array(saleTicketPaymentSchema),
  /** Every revision with lines, oldest first (the last is the current one). */
  revisions: z.array(saleTicketRevisionSchema),
});
/** Response of `GET /sales/tickets/:id`. */
export type SaleTicketDetail = z.infer<typeof saleTicketDetailSchema>;
